import { COMMON_ERR_NETWORK, images } from "@constants";
import { ETabName } from "@pages/MasterDataPage/constants";
import { masterDataApi } from "@services/masterDataApi";
import { appToast } from "@utils/appToast";
import { COMMON_CONSTANT } from "@utils/constants";
import {
  handleErrorCodeImportCameraFile,
  handleErrorCodeImportCompanyFile,
  handleErrorCodeImportProcessingCenterFile,
  handleErrorCodeImportSectorFile,
  handleErrorCodeImportStoreFile,
  handleErrorCodeImportUserFile,
  handleShowToastError,
} from "@utils/helpers";
import { Button, Upload } from "antd";
import { t } from "i18next";
import { useRef, useState } from "react";
import { DataTabWrapper, ModalFailWrapper } from "../MasterDataPageStyled";

type TDataTabProps = {
  tabName: string;
};

const IMPORT_SUCCESS_MSG_KEY = "COMMON_MSG.IMPORT_SUCCESS";

function DataTab({ tabName }: TDataTabProps) {
  // Import file
  const importInputRef = useRef<HTMLInputElement>(null);
  const [isImport, setIsImport] = useState(false);
  const [modalUploadFail, setModalUploadFail] = useState(false);
  const [_errorColumns, setErrorColumns] = useState<string[]>([]);
  const [errorRow, setErrorRow] = useState(0);
  const [description, setDescription] = useState<string | undefined>("");

  const handleImport = async () => {
    importInputRef.current?.click();
  };

  const handleImportCompany = async (file: any) => {
    try {
      const response = await masterDataApi.importCompany(file);
      if (response.ok) {
        appToast.success({ message: t(IMPORT_SUCCESS_MSG_KEY) });
      } else {
        setModalUploadFail(true);
        const errorCol = response.data.errors.map((err: any) => err.columnName);
        setErrorRow(response?.errors?.[0]?.rowIndex);
        setErrorColumns(errorCol);
      }
    } catch (error: any) {
      if (error !== COMMON_ERR_NETWORK) {
        const descriptionParsed = handleErrorCodeImportCompanyFile(error);
        setDescription(descriptionParsed);
        setErrorRow(error.data?.errors?.[0].rowIndex);
        setModalUploadFail(true);
      } else {
        setModalUploadFail(false);
        handleShowToastError(error, "");
      }
    } finally {
      setIsImport(false);
    }
  };

  const handleImportSector = async (file: any) => {
    try {
      const response = await masterDataApi.importSector(file);
      if (response.ok) {
        appToast.success({ message: t(IMPORT_SUCCESS_MSG_KEY) });
      } else {
        setModalUploadFail(true);
        const errorCol = response.errors.map((err) => err.columnName);
        setErrorRow(response.errors?.[0].rowIndex);
        setErrorColumns(errorCol);
      }
    } catch (error: any) {
      if (error !== COMMON_ERR_NETWORK) {
        const descriptionParsed = handleErrorCodeImportSectorFile(error);
        setDescription(descriptionParsed);
        setErrorRow(error.data.errors?.[0].rowIndex);
        setModalUploadFail(true);
      } else {
        setModalUploadFail(false);
        handleShowToastError(error, "");
      }
    } finally {
      setIsImport(false);
    }
  };

  const handleImportProcessCenter = async (file: any) => {
    try {
      const response = await masterDataApi.importProcessCenter(file);
      if (response.ok) {
        appToast.success({ message: t(IMPORT_SUCCESS_MSG_KEY) });
      } else {
        setModalUploadFail(true);
        const errorCol = response.errors.map((err) => err.columnName);
        setErrorRow(response.errors?.[0].rowIndex);
        setErrorColumns(errorCol);
      }
    } catch (error: any) {
      if (error !== COMMON_ERR_NETWORK) {
        const descriptionParsed =
          handleErrorCodeImportProcessingCenterFile(error);
        setDescription(descriptionParsed);
        setErrorRow(error.data.errors?.[0].rowIndex);
        setModalUploadFail(true);
      } else {
        setModalUploadFail(false);
        handleShowToastError(error, "");
      }
    } finally {
      setIsImport(false);
    }
  };

  const handleImportUser = async (file: File) => {
    try {
      const response = await masterDataApi.importUser(file);
      if (response.ok) {
        appToast.success({ message: t(IMPORT_SUCCESS_MSG_KEY) });
      } else {
        setModalUploadFail(true);
        const errorCol = response.errors.map((err) => err.columnName);
        setErrorRow(response.errors?.[0].rowIndex);
        setErrorColumns(errorCol);
      }
    } catch (error: any) {
      if (error !== COMMON_ERR_NETWORK) {
        const descriptionParsed = handleErrorCodeImportUserFile(error);
        setDescription(descriptionParsed);
        setErrorRow(error.data.errors?.[0].rowIndex);
        setModalUploadFail(true);
      } else {
        setModalUploadFail(false);
        handleShowToastError(error, "");
      }
    } finally {
      setIsImport(false);
    }
  };

  const handleImportStore = async (file: File) => {
    try {
      const response = await masterDataApi.importStore(file);
      if (response.ok) {
        appToast.success({ message: t(IMPORT_SUCCESS_MSG_KEY) });
      } else {
        setModalUploadFail(true);
        const errorCol = response.errors.map((err: any) => err.columnName);
        setErrorRow(response?.errors?.[0]?.rowIndex);
        setErrorColumns(errorCol);
      }
    } catch (error: any) {
      if (error !== COMMON_ERR_NETWORK) {
        const descriptionParsed = handleErrorCodeImportStoreFile(error);
        setDescription(descriptionParsed);
        setErrorRow(error.data.errors?.[0].rowIndex);
        setModalUploadFail(true);
      } else {
        setModalUploadFail(false);
        handleShowToastError(error, "");
      }
    } finally {
      setIsImport(false);
    }
  };

  const handleImportCamera = async (file: File) => {
    try {
      const response = await masterDataApi.importCamera(file);

      if (response.ok) {
        appToast.success({ message: t(IMPORT_SUCCESS_MSG_KEY) });
      } else {
        setModalUploadFail(true);
        const errorCol = response.errors.map((err) => err.columnName);
        setErrorRow(response.errors?.[0].rowIndex);
        setErrorColumns(errorCol);
      }
    } catch (error: any) {
      if (error !== COMMON_ERR_NETWORK) {
        const descriptionParsed = handleErrorCodeImportCameraFile(error);
        setDescription(descriptionParsed);
        setErrorRow(error?.data?.errors?.[0].rowIndex);
        setModalUploadFail(true);
      } else {
        setModalUploadFail(false);
        // Error disconnect internet or server error
        handleShowToastError(error, "");
      }
    } finally {
      setIsImport(false);
    }
  };

  const handleCallApiImport = async (file: any) => {
    setIsImport(true);
    switch (tabName) {
      case ETabName.COMPANY:
        // import company
        await handleImportCompany(file);
        return;
      case ETabName.SECTOR:
        // import SECTOR
        await handleImportSector(file);
        return;
      case ETabName.PROCESS_CENTER:
        // import PROCESS_CENTER
        await handleImportProcessCenter(file);
        return;
      case ETabName.AREA:
        // import AREA
        await handleImportSector(file);
        // await handleImportArea(file);
        return;
      case ETabName.STORE:
        // import STORE
        await handleImportStore(file);
        return;
      case ETabName.USER:
        // import USER
        await handleImportUser(file);
        return;
      case ETabName.CAMERA:
        // import CAMERA
        await handleImportCamera(file);
        return;
      default:
        // import company
        await handleImportCompany(file);
        return;
    }
  };

  const handleChangeInputImport = async (fileImport: any) => {
    const KB_TO_MB = 1024; // Conversion factor from KB to MB
    const maxFileSizeMB = 10; // Maximum file size in MB
    const fileSizeInKB = fileImport.size / 1024;
    const fileSizeInMB = fileSizeInKB / KB_TO_MB;

    if (!fileImport) {
      return;
    }
    if (fileImport.type !== "text/csv") {
      setModalUploadFail(true);
      setDescription(
        COMMON_CONSTANT.IMPORT_MASTER_DATA_MESSAGE.COMMON_WRONG_FILE_FORMAT_MSG
      );
      setErrorRow(0);
      return;
    }
    if (fileSizeInMB >= maxFileSizeMB) {
      setModalUploadFail(true);
      setDescription(
        COMMON_CONSTANT.IMPORT_MASTER_DATA_MESSAGE.COMMON_WRONG_FILE_SIZE_MSG
      );
      setErrorRow(0);
      return;
    }
    await handleCallApiImport(fileImport);
  };

  return (
    <>
      <DataTabWrapper>
        <div className="master-data_tab-content">
          <img src={images.importData} alt="import_data" />
          <p style={{ color: "#475467", fontWeight: "500" }}>
            {t("V_SHOT006.FORMAT_IMPORT")}
          </p>
          <div className="master-data_tab-content-btn">
            <Upload
              beforeUpload={handleChangeInputImport}
              showUploadList={false}
              customRequest={({ onSuccess }: any) => onSuccess("")}
            >
              <Button
                loading={isImport}
                disabled={isImport}
                onClick={handleImport}
              >
                {t("COMMON.IMPORT")}
              </Button>
            </Upload>
          </div>
        </div>
      </DataTabWrapper>
      <ModalFailWrapper
        centered
        open={modalUploadFail}
        title={
          <span
            style={{ fontWeight: "700", color: "var(--color-modal-title)" }}
          >
            {t("IMPORT_FAIL.TITLE")}
          </span>
        }
        onOk={() => {
          setModalUploadFail(false);
          setErrorColumns([]);
        }}
        maskClosable={false}
        okText={t("COMMON.CLOSE")}
        width={416}
        closable={true}
        closeIcon={true}
        onCancel={() => {
          setModalUploadFail(false);
          setErrorColumns([]);
        }}
      >
        <img src={images.importDataFail} alt="master-data_fail" />
        <div className="master-data_fail-message">
          {errorRow === null || !errorRow || errorRow === 0 ? (
            <span style={{ color: "#475467" }}>{description}</span>
          ) : (
            <>
              <p style={{ paddingBottom: "4px" }}>
                {errorRow ? errorRow : ""} {t("IMPORT_FAIL.ERROR_LINE")}
              </p>
              <span style={{ color: "#475467" }}>{description}</span>
            </>
          )}
        </div>
      </ModalFailWrapper>
    </>
  );
}

export { DataTab };
